import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { DatePicker, Divider, Form, Input, notification } from "antd";
import AwesomeDebouncePromise from "awesome-debounce-promise";
import {
    CloseCircleFilled,
    InfoOutlined,
    MailFilled,
    StopFilled,
    StarFilled,
    EyeInvisibleFilled,
} from "@ant-design/icons";
import { IUserDetails, IUserPayload } from "@Models/IUserDetails";
import UserService from "@Services/UserService";
import { MainContext } from "@Context/provider";
import { SkeletonRows } from "@Components/shared/SkeletonRows";
import InspectorModuleTitle from "@Components/groups/InspectorModuleTitle";
import { InfiniteData, useMutation, useQueryClient } from "@tanstack/react-query";
import { GroupContext, QueryKeys, SystemContext, UserContext } from "@Constants";
import SpecialtySelect from "@Components/user/SpecialtySelect";
import CountrySelect from "@Components/user/CountrySelect";
import InspectorInfoField from "@Components/groups/InspectorInfoField";
import CountryFlag from "@Components/user/CountryFlag";
import { Countries } from "../../countries";
import moment from "moment/moment";
import GenderSelect from "@Components/user/GenderSelect";
import { hasCapability, prettifyName } from "@Utils";
import { ICheckCapFull } from "./types";
import { QueryKeys as GroupsQueryKeys } from "@Features/groups/constants";

interface IProps {
    instance: string;
    checkCap: ICheckCapFull;
}

const ModuleUser: React.FC<IProps> = ({ instance, checkCap }: IProps) => {
    const { ui: uiState } = useContext(MainContext);
    const queryClient = useQueryClient();
    const [data, setData] = useState<IUserDetails>(null);
    const [fetching, setFetching] = useState<boolean>(false);
    const [updating, setUpdating] = useState<boolean>(false);

    const stateRef = useRef<IUserDetails>();
    stateRef.current = data;
    const allowUpdate = checkCap("User/UpdateUser");

    /**
     * Return the currently selected context
     */
    const getCurrentContext = () => {
        if (uiState.currentGroup === null) return SystemContext;
        if (uiState.currentUser === null) return GroupContext;
        return UserContext;
    };

    const canViewPrivate = hasCapability("User/GetPrivate", getCurrentContext());

    /**
     * Update the model with edited data.
     */
    const updateField = useMutation(
        async () => {
            setUpdating(true);
            const service = new UserService();

            // Dont update if full name is empty
            if (stateRef.current?.fullName === "") {
                notification.error({ message: "User update failed. Full name is required." });
                setUpdating(false);
                fetch().then(() => {
                    setFetching(false);
                });
                // Force the update in the groups list
                await queryClient.invalidateQueries([QueryKeys.GroupMembers, uiState.currentGroup.instance]);
                await queryClient.invalidateQueries([GroupsQueryKeys.GroupUsers, uiState.currentGroup.instance]);

                throw new Error("Full name is required");
            }
            await service.update(stateRef.current);
            setUpdating(false);
        },
        {
            onSuccess: async () => {
                notification.success({ message: "User updated." });
                // Force the update in the groups list
                await queryClient.invalidateQueries([QueryKeys.GroupMembers, uiState.currentGroup.instance]);
                await queryClient.invalidateQueries([GroupsQueryKeys.GroupUsers, uiState.currentGroup.instance]);
            },
        }
    );

    /**
     * Trigger an update two seconds after last keypress.
     */
    const debouncePromise = useCallback(AwesomeDebouncePromise(updateField.mutate, 2000), []);

    /**
     * Trigger an update to the form data state
     * @param field
     * @param value
     */
    const onInputChange = async (field, value) => {
        const formData = { ...data };
        formData[field] = value;
        setData(formData);
        await debouncePromise();
    };

    const renderSkeleton = () => {
        return (
            <>
                <SkeletonRows rowHeight={32} count={5} />
                <Divider />
                <SkeletonRows rowHeight={32} count={3} />
                <Divider />
                <SkeletonRows rowHeight={32} count={3} />
                <Divider />
                <SkeletonRows rowHeight={32} count={1} />
            </>
        );
    };

    const renderStatus = (status: string) => {
        switch (status) {
            case "invited":
                return (
                    <InspectorInfoField color={"cyan"}>
                        <span>
                            <MailFilled style={{ marginRight: "10px" }} />
                            Invited
                        </span>
                    </InspectorInfoField>
                );
            case "active":
                return (
                    <InspectorInfoField color={"green"}>
                        <span>
                            <StarFilled style={{ marginRight: "10px" }} />
                            Active
                        </span>
                    </InspectorInfoField>
                );
            case "deactivated":
                return (
                    <InspectorInfoField color={"yellow"}>
                        <span>
                            <EyeInvisibleFilled style={{ marginRight: "10px" }} />
                            Deactivated
                        </span>
                    </InspectorInfoField>
                );
            case "suspended":
                return (
                    <InspectorInfoField color={"red"}>
                        <span>
                            <StopFilled style={{ marginRight: "10px" }} />
                            Suspended
                        </span>
                    </InspectorInfoField>
                );
            case "deleted":
                return (
                    <InspectorInfoField color={"red"}>
                        <span>
                            <CloseCircleFilled style={{ marginRight: "10px" }} />
                            Deleted
                        </span>
                    </InspectorInfoField>
                );
            default:
                return (
                    <InspectorInfoField color={data?.status == null ? "green" : "blue"}>
                        <span>
                            <StarFilled style={{ marginRight: "10px" }} />
                            {data?.status == null ? "Active" : prettifyName(data?.status)}
                        </span>
                    </InspectorInfoField>
                );
        }
    };

    const fetch = async () => {
        setFetching(true);
        const service = new UserService();
        const user = canViewPrivate
            ? await service.getPrivate([instance], false)
            : await service.getPublic([instance], false, false);
        if (!user.hasErrors) {
            stateRef.current = user.value[0];
            if (stateRef.current !== null) {
                setData(stateRef.current);
            }
        }
    };

    /**
     * Fetch current user public data.
     */
    useEffect(() => {
        stateRef.current = null;
        setData(null);

        const query = queryClient.getQueryData<InfiniteData<IUserPayload>>([
            QueryKeys.GroupMembers,
            uiState.currentGroup.instance,
        ]);
        const user = query?.pages
            ?.map((x) => x.users)
            ?.flat()
            ?.find((x) => x.userId === instance);
        if (user !== undefined && user !== null) {
            stateRef.current = user;
            if (stateRef.current !== null) {
                setData(stateRef.current);
            }
        } else {
            fetch().then(() => {
                setFetching(false);
            });
        }
    }, [uiState.currentUser]);

    return (
        <>
            <div style={{ padding: "12px 16px" }}>
                <InspectorModuleTitle
                    title={"User Details"}
                    slug={"user"}
                    fetching={fetching || updating}
                    icon={<InfoOutlined />}
                />
                {fetching ? (
                    renderSkeleton()
                ) : (
                    <Form className="inspector-form" initialValues={{}}>
                        <Form.Item label="Full name" rules={[{ required: true, message: `Full name is required` }]}>
                            {allowUpdate ? (
                                <Input
                                    name={"fullName"}
                                    value={data?.fullName}
                                    onChange={(e) => onInputChange(e.target.name, e.target.value)}
                                />
                            ) : (
                                <InspectorInfoField>{data?.fullName}</InspectorInfoField>
                            )}
                        </Form.Item>
                        <Form.Item label="Status">{renderStatus(data?.status)}</Form.Item>
                        <Form.Item label="Email">
                            <InspectorInfoField>{data?.email?.toLowerCase()}</InspectorInfoField>
                        </Form.Item>
                        <Form.Item label="Organisation">
                            {allowUpdate ? (
                                <Input
                                    name={"organisation"}
                                    value={data?.organisation}
                                    onChange={(e) => onInputChange(e.target.name, e.target.value)}
                                />
                            ) : (
                                <InspectorInfoField>{data?.organisation}</InspectorInfoField>
                            )}
                        </Form.Item>
                        <Form.Item label={"Country"} rules={[{ required: true, message: `Country is required` }]}>
                            {allowUpdate ? (
                                <CountrySelect
                                    value={data?.countryCode}
                                    onChange={(value) => onInputChange("countryCode", value)}
                                />
                            ) : (
                                <InspectorInfoField>
                                    <span>
                                        <CountryFlag countryCode={data?.countryCode ?? "GB"} />
                                    </span>
                                    <span style={{ paddingLeft: "10px" }}>
                                        {Countries.get(data?.countryCode ?? "GB")}
                                    </span>
                                </InspectorInfoField>
                            )}
                        </Form.Item>
                        <Divider />
                        {canViewPrivate && (
                            <>
                                <Form.Item
                                    label="Nickname"
                                    rules={[{ required: true, message: `Nickname is required` }]}
                                >
                                    {allowUpdate ? (
                                        <Input
                                            name={"nickName"}
                                            value={data?.nickName}
                                            onChange={(e) => onInputChange(e.target.name, e.target.value)}
                                        />
                                    ) : (
                                        <InspectorInfoField>{data?.nickName}</InspectorInfoField>
                                    )}
                                </Form.Item>
                                <Form.Item
                                    label="Date of birth"
                                    rules={[{ required: true, message: `Please enter a valid date of birth` }]}
                                >
                                    {allowUpdate ? (
                                        <DatePicker
                                            value={moment(data?.dateOfBirth, moment.ISO_8601)}
                                            onChange={(date) => onInputChange("dateOfBirth", date?.toISOString())}
                                            picker="date"
                                            format={"MMM Do YYYY"}
                                            allowClear={false}
                                        />
                                    ) : (
                                        <InspectorInfoField>
                                            {moment(data?.dateOfBirth).format("MMM Do YYYY")}
                                        </InspectorInfoField>
                                    )}
                                </Form.Item>
                                <Form.Item label="Gender">
                                    {allowUpdate ? (
                                        <GenderSelect
                                            value={data?.gender}
                                            onChange={(value) => onInputChange("gender", value)}
                                        />
                                    ) : (
                                        <InspectorInfoField>{data?.gender}</InspectorInfoField>
                                    )}
                                </Form.Item>
                            </>
                        )}
                        <Divider />
                        <Form.Item label="Specialty">
                            {allowUpdate ? (
                                <SpecialtySelect
                                    value={data?.specialty}
                                    onChange={(value) => onInputChange("specialty", value)}
                                />
                            ) : (
                                <InspectorInfoField>{data?.specialty}</InspectorInfoField>
                            )}
                        </Form.Item>
                        <Form.Item label="Years training">
                            {allowUpdate ? (
                                <Input
                                    name={"yearsTraining"}
                                    type="number"
                                    value={data?.yearsTraining}
                                    onChange={(e) => onInputChange(e.target.name, e.target.value)}
                                />
                            ) : (
                                <InspectorInfoField>{data?.yearsTraining}</InspectorInfoField>
                            )}
                        </Form.Item>
                        <Form.Item label="Medical school">
                            {allowUpdate ? (
                                <Input
                                    name={"medicalSchool"}
                                    value={data?.medicalSchool}
                                    onChange={(e) => onInputChange(e.target.name, e.target.value)}
                                />
                            ) : (
                                <InspectorInfoField>{data?.medicalSchool}</InspectorInfoField>
                            )}
                        </Form.Item>
                        <Divider />
                        <Form.Item label="Guid">
                            <InspectorInfoField>{data?.userGuid}</InspectorInfoField>
                        </Form.Item>
                    </Form>
                )}
            </div>
            <hr />
        </>
    );
};
export default ModuleUser;
